// src/hooks/useNetworkStatus.js
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabase';

/**
 * Hook to track network/online status
 * Checks both browser online status and Supabase connectivity
 */
export const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSupabaseConnected, setIsSupabaseConnected] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);

  // Check Supabase connectivity
  const checkSupabaseConnection = useCallback(async () => {
    if (!navigator.onLine) {
      setIsSupabaseConnected(false);
      return false;
    }

    try {
      // Simple health check - just check if we can reach Supabase
      const { error } = await supabase.from('user_settings').select('user_id').limit(1).maybeSingle();

      // Even if there's no data, as long as there's no network error, we're connected
      const connected = !error || !error.message?.includes('fetch');
      setIsSupabaseConnected(connected);
      setLastChecked(new Date());
      return connected;
    } catch (err) {
      setIsSupabaseConnected(false);
      setLastChecked(new Date());
      return false;
    }
  }, []);

  // Handle browser online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Check Supabase when coming back online
      checkSupabaseConnection();
    };

    const handleOffline = () => {
      setIsOnline(false);
      setIsSupabaseConnected(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial check
    checkSupabaseConnection();

    // Periodic check every 30 seconds when online
    const interval = setInterval(() => {
      if (navigator.onLine) {
        checkSupabaseConnection();
      }
    }, 30000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, [checkSupabaseConnection]);

  return {
    isOnline,
    isSupabaseConnected,
    isFullyConnected: isOnline && isSupabaseConnected,
    lastChecked,
    checkConnection: checkSupabaseConnection,
  };
};

export default useNetworkStatus;
